/**
 * 飞书客户端环境检测、H5 JS-SDK 加载与飞书 JSAPI 自动登录抑制。
 *
 * isFeishuClient 用于判断用户是否在飞书/Lark 客户端内嵌浏览器中打开页面。
 * 飞书客户端内自动注入 window.h5sdk / window.tt；普通浏览器没有。
 *
 * 参考: ai-workbench (Javis) lib/feishu.ts isFeishuClient/loadFeishuSDK；
 *       knox wechat-browser.ts 的抑制机制（登出防自动登录死循环）。
 */

declare global {
  interface Window {
    /** 飞书 h5-js-sdk 对象。客户端内自动注入，或通过 CDN 引入。 */
    h5sdk?: {
      ready: (callback: () => void) => void;
      config?: (opts: unknown) => void;
      error?: (callback: (err: unknown) => void) => void;
    };
    /** 飞书 tt 命名空间（JSAPI 调用入口）。 */
    tt?: {
      /** 新版免登接口（V6.9.0+），优先使用。 */
      requestAccess?: (params: {
        appID: string;
        scopeList?: string[];
        state?: string;
        success?: (res: { code: string }) => void;
        fail?: (err: { errno?: number; errString?: string; errMsg?: string }) => void;
      }) => void;
      /** 旧版免登接口（requestAccess 不可用或客户端版本过低时降级）。 */
      requestAuthCode?: (params: {
        appId?: string;
        success?: (res: { code: string }) => void;
        fail?: (err: { errMsg?: string }) => void;
      }) => void;
    };
  }
}

/**
 * 是否在飞书/Lark 客户端内嵌浏览器中。
 * 三重检测：UA（最可靠）→ window.h5sdk → window.tt。
 */
export function isFeishuClient(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('lark/') || ua.includes('feishu')) return true;
  if (typeof window.h5sdk !== 'undefined') return true;
  if (typeof window.tt !== 'undefined') return true;
  return false;
}

const FEISHU_SDK_CDN = 'https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.34.js';

/**
 * 动态加载飞书 h5-js-sdk。客户端内通常已自动注入，否则从 CDN 加载。
 * 已加载则直接 resolve。
 */
export function loadFeishuSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Feishu SDK requires a browser environment'));
      return;
    }
    if (window.h5sdk || window.tt) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="h5-js-sdk"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Feishu SDK')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = FEISHU_SDK_CDN;
    script.async = true;
    script.onload = () => {
      // 给 SDK 一点初始化时间
      setTimeout(() => {
        if (window.h5sdk || window.tt) {
          resolve();
        } else {
          reject(new Error('Feishu SDK loaded but window.h5sdk unavailable'));
        }
      }, 100);
    };
    script.onerror = () => reject(new Error('Failed to load Feishu SDK script'));
    document.head.appendChild(script);
  });
}

/**
 * 用飞书 JSAPI 获取免登授权码。
 * 优先 requestAccess（新，V6.9.0+），errno===103（客户端版本过低）降级 requestAuthCode。
 * 必须在 window.h5sdk.ready 回调后调用（飞书官方要求）。
 */
export function requestFeishuAuthCode(appId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.h5sdk) {
      reject(new Error('飞书 h5sdk 不可用'));
      return;
    }

    window.h5sdk.ready(() => {
      const tt = window.tt;
      if (!tt) {
        reject(new Error('飞书 tt 命名空间不可用'));
        return;
      }

      // 优先 requestAccess（官方推荐）
      if (typeof tt.requestAccess === 'function') {
        tt.requestAccess({
          appID: appId,
          scopeList: [], // 空 = 仅授予获取登录用户信息权限
          success: (res) => res.code ? resolve(res.code) : reject(new Error('飞书授权码为空')),
          fail: (err) => {
            // errno 103 = 客户端版本过低不支持 requestAccess → 降级 requestAuthCode
            if (err.errno === 103) {
              callRequestAuthCode(appId, resolve, reject);
            } else {
              reject(new Error(`飞书授权失败: ${err.errString || err.errno || '未知错误'}`));
            }
          },
        });
      } else if (typeof tt.requestAuthCode === 'function') {
        // JSSDK 版本过低不支持 requestAccess → 直接用 requestAuthCode
        callRequestAuthCode(appId, resolve, reject);
      } else {
        reject(new Error('飞书 tt.requestAccess/requestAuthCode 均不可用'));
      }
    });
  });
}

function callRequestAuthCode(
  appId: string,
  resolve: (code: string) => void,
  reject: (err: Error) => void,
): void {
  const tt = window.tt;
  if (!tt || typeof tt.requestAuthCode !== 'function') {
    reject(new Error('飞书 tt.requestAuthCode 不可用'));
    return;
  }
  tt.requestAuthCode({
    appId,
    success: (res) => (res.code ? resolve(res.code) : reject(new Error('飞书授权码为空'))),
    fail: (err) => reject(new Error(`飞书免登失败: ${err.errMsg || '未知错误'}`)),
  });
}

// ---- 自动登录抑制（防登出死循环，照搬 wechat-browser.ts）----
// sessionStorage key：标记用户刚主动登出，抑制飞书 JSAPI 自动免登。
// 背景：LoginPage 在飞书客户端内未登录时会自动触发 jsapi 免登，但用户主动登出后，
// 飞书侧仍保留授权，会静默重新登录，导致"退出后又自动登录"死循环，
// 且飞书客户端内永远切不到钉钉/微信等其他登录方式。
// logout 时 set，LoginPage 自动免登 effect 据此跳过；用户主动点飞书按钮时清除。
const FEISHU_AUTO_LOGIN_SUPPRESS_KEY = 'knox_feishu_auto_login_suppressed';

/** 标记当前会话需抑制飞书 JSAPI 自动登录（在 logout 时调用）。 */
export function markFeishuAutoLoginSuppressed(): void {
  try {
    sessionStorage.setItem(FEISHU_AUTO_LOGIN_SUPPRESS_KEY, '1');
  } catch {
    // sessionStorage 不可用时静默忽略
  }
}

/** 是否应抑制飞书 JSAPI 自动登录（LoginPage 自动免登 effect 据此跳过）。 */
export function isFeishuAutoLoginSuppressed(): boolean {
  try {
    return sessionStorage.getItem(FEISHU_AUTO_LOGIN_SUPPRESS_KEY) === '1';
  } catch {
    return false;
  }
}

/** 清除抑制标记（用户主动点飞书登录按钮时调用，恢复正常自动登录语义）。 */
export function clearFeishuAutoLoginSuppressed(): void {
  try {
    sessionStorage.removeItem(FEISHU_AUTO_LOGIN_SUPPRESS_KEY);
  } catch {
    // no-op
  }
}

// 确保此文件被识别为模块（含全局 declare）
export {};
